/**
 * Image with Accordion FAQ Web Component
 * Handles custom accordion functionality and mobile slider using modern web components
 */

class ImageAccordionFAQComponent extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.resizeTimeout = null;
  }

  // Called when component is added to the DOM
  connectedCallback() {
    this.initializeMobileSlider();
    this.setupResizeHandler();
  }

  // Called when component is removed from the DOM
  disconnectedCallback() {
    this.cleanup();
  }

  initializeMobileSlider() {
    const swiperContainer = this.querySelector('.accordion-faq-swiper');

    if (!swiperContainer) return;

    // Get autoplay setting from data attribute
    const autoplayEnabled = swiperContainer.dataset.autoplay === 'true';

    // Base Swiper configuration
    const swiperConfig = {
      // Swiper configuration
      loop: true,
      slidesPerView: 1.5,
      spaceBetween: 20,
      centeredSlides: false,

      // Pagination
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
        dynamicBullets: false,
      },

      // Touch settings
      touchRatio: 1,
      simulateTouch: true,
      grabCursor: true,

      // Transition
      speed: 300,
      effect: 'slide',

      // Auto height
      autoHeight: false,

      // Breakpoints for different screen sizes
      breakpoints: {
        320: {
          slidesPerView: 1.5,
          spaceBetween: 16,
        },
        480: {
          slidesPerView: 1.5,
          spaceBetween: 20,
        },
        768: {
          slidesPerView: 1.5,
          spaceBetween: 24,
        }
      }
    };

    // Add autoplay configuration if enabled
    if (autoplayEnabled) {
      swiperConfig.autoplay = {
        delay: 3000, // 3 seconds delay between slides
        disableOnInteraction: false, // Continue autoplay after user interaction
        pauseOnMouseEnter: true, // Pause when user hovers
      };
    }

    // Initialize Swiper for mobile
    this.swiper = new Swiper(swiperContainer, swiperConfig);
  }

  // Setup resize handler for this component
  setupResizeHandler() {
    this.resizeHandler = () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, 250);
    };
    window.addEventListener('resize', this.resizeHandler);
  }

  // Handle window resize
  handleResize() {
    if (this.swiper) {
      this.swiper.update();
    }
  }

  // Cleanup method called when component is removed
  cleanup() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  // Public methods for external access
  play() {
    if (this.updatePlayState) {
      this.updatePlayState(true);
    }
  }

  pause() {
    if (this.updatePlayState) {
      this.updatePlayState(false);
    }
  }

  toggle() {
    if (this.updatePlayState) {
      this.updatePlayState(!this.isPlaying);
    }
  }

  refresh() {
    this.cleanup();
    this.connectedCallback();
  }
}

// Register the custom element
customElements.define('image-accordion-faq-component', ImageAccordionFAQComponent);

// Handle Shopify theme editor events for web components
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    // Refresh all FAQ components when sections are loaded in theme editor
    document.querySelectorAll('image-accordion-faq-component').forEach(component => {
      component.refresh();
    });
  });

  document.addEventListener('shopify:section:unload', () => {
    // Components will automatically cleanup via disconnectedCallback
  });

  document.addEventListener('shopify:section:select', () => {
    // Update all FAQ components when sections are selected in theme editor
    document.querySelectorAll('image-accordion-faq-component').forEach(component => {
      component.handleResize();
    });
  });
}